/**
 * @author NikolaV
 */
import { action, autorun, makeObservable, observable } from 'mobx';
import { GeofencesStore } from './GeofencesStore';
import { MaterialsStore } from './MaterialsStore';
import { TasksStore } from './TasksStore';
import { TrucksStore } from './TrucksStore';
import { LStorage } from '@shared/Storage';
import { FilterStore } from './FilterStore';
import { mapWrapper } from '@pages/trucks/TrucksMap';
import { WindowManager } from '@shared/ui-react/FloatingWindow';


export class TrucksPageStore {
    @observable public isMapCleared = true;
    @observable public activeSidebarTabIndex = 0;
    @observable public activePanel: 'trips' | 'stats' = null;
    @observable public isPanelFullscreen = false;
    @observable public isPanelMinimized = false;
    @observable.shallow public drawTripsTracks: ITruckTrip[] = null;
    @observable public bgMapsOpacity: number = 100;

    @observable public showGeofenceNamesOnMap: boolean = true;
    @observable public compactListViews: boolean = false;
    @observable public sidebarCollapsed: boolean = false;
    @observable public isMobile: boolean = false;
    public tripsTableScrollTop: number = 0;

    @observable public tripsColumnsHidden: string[] = [
        'startingAreaGpsName', 'endingAreaGpsName', 'username', 'cycleTime', 'unloadedTime'
    ];

    public materials: MaterialsStore;
    public tasks: TasksStore;
    public vehicles: TrucksStore;
    public geofences: GeofencesStore;
    public tripsFilter: FilterStore;

    private readonly storagePrefix = 'trucks_' + kuura.getProjectId() + '_';

    constructor() {
        makeObservable(this);
        this.tasks = new TasksStore();
        this.vehicles = new TrucksStore();
        this.geofences = new GeofencesStore();
        this.tripsFilter = new FilterStore();
        this.materials = new MaterialsStore(this.geofences);

        window.addEventListener('resize', _.throttle(() => {
            this.isMobile = window.innerWidth < 900;

            if (!this.isMobile && this.activeSidebarTabIndex === null) {
                this.activeSidebarTabIndex = 0;
            }
        }, 100));
    }

    public init() {
        $.when(
            this.vehicles.fetchWithStats(),
            this.materials.fetch(),
            this.tasks.fetch(),
            this.geofences.fetch())
        .done(() => {
            this.tripsFilter.setFromStoresData();

            this.loadStateFromUrl();
            this.loadState();

            autorun(this.saveState, { delay: 100 });

            autorun(() => {
                mapWrapper.backgroundMaps.setOpacity(this.bgMapsOpacity);
            }, { delay: 1 });
        });
    }

    @action.bound
    closePanel() {
        this.activePanel = null;
        setTimeout(() => {
            // we need to wait for panel to unmount, so it wont fetch stats again on close because filters are reset
            this.tripsFilter.deselectVehicles();
            this.isPanelFullscreen = false;
            this.isPanelMinimized = false;
            this.drawTripsTracks = null;
        });
    }

    @action.bound
    showTrips(vehicleId: number) {
        this.activePanel = 'trips';
        this.isPanelFullscreen = false;
        this.isPanelMinimized = false;
        this.drawTripsTracks = null;

        if (vehicleId === -1) {
            this.tripsFilter.selectAll();
        }
        else {
            this.tripsFilter.focusVehicle(vehicleId);
        }

        WindowManager.closeAll();
        if (this.isMobile) {
            this.activeSidebarTabIndex = null;
        }
    }

    @action.bound
    showVehicleStats(vehicleId: number) {
        this.activePanel = 'stats';
        this.isPanelFullscreen = false;
        this.isPanelMinimized = false;
        this.drawTripsTracks = null;

        if (vehicleId === -1) {
            this.tripsFilter.selectAll();
        }
        else {
            this.tripsFilter.focusVehicle(vehicleId);
        }
        WindowManager.closeAll();
        if (this.isMobile) {
            this.activeSidebarTabIndex = null;
        }
    }

    @action.bound
    showAreaStats(areaId: number) {
        this.activePanel = 'stats';
        this.isPanelFullscreen = false;
        this.isPanelMinimized = false;
        this.tripsFilter.focusArea(areaId);
        WindowManager.closeAll();
        if (this.isMobile) {
            this.activeSidebarTabIndex = null;
        }
    }

    @action.bound
    togglePanelFullscreen() {
        this.isPanelFullscreen = !this.isPanelFullscreen;
    }

    @action.bound
    activateTab(index: number = 0) {
        this.activeSidebarTabIndex = index;
        this.sidebarCollapsed = false;
        this.closePanel();
        WindowManager.closeAll();
    }

    saveState = () => {
        if (mapWrapper.tileLayers.activeUuids[0]) {
            const uuid = mapWrapper.tileLayers.activeUuids[0];
            const opacity = mapWrapper.tileLayers.opacities[uuid];
            LStorage.setItem(this.storagePrefix + 'baselayerIndex', uuid);
            LStorage.setItem(this.storagePrefix + 'baselayerOpacity', opacity);
        }
        LStorage.setItem(this.storagePrefix + 'activeSidebarTabIndex', this.activeSidebarTabIndex + '');
        LStorage.setItem(this.storagePrefix + 'activePanel', this.activePanel + '');
        //LStorage.setItem(this.storagePrefix + 'groupBy', this.stats.groupBy);
        LStorage.setItem(this.storagePrefix + 'showGeofenceNamesOnMap', this.showGeofenceNamesOnMap + '');
        LStorage.setItem(this.storagePrefix + 'compactListViews', this.compactListViews + '');
        LStorage.setItem(this.storagePrefix + 'vehiclesSortKey', this.vehicles.sortKey);
        LStorage.setItem(this.storagePrefix + 'geofencesSortKey', this.geofences.sortKey);
        LStorage.setItem(this.storagePrefix + 'bgMapsOpacity', this.bgMapsOpacity);
        LStorage.setItem(this.storagePrefix + 'tripsColumnsHidden', this.tripsColumnsHidden.join(','));

        // url hash
        let url = `panel=${this.activePanel}&${this.tripsFilter.urlString}`;
        if (!this.activePanel) {
            url = '';
        }
        window.history.replaceState(null, null, location.origin + location.pathname + '#' + url);
    };

    @action.bound
    loadState() {
        const baselayerIndex = LStorage.getOrDefault(this.storagePrefix + 'baselayerIndex', '-1');
        const baselayerOpacity = LStorage.getOrDefault(this.storagePrefix + 'baselayerOpacity', '100');
        const bgMapsOpacity = LStorage.getOrDefault(this.storagePrefix + 'bgMapsOpacity', '100');
        const sidebarPanelIndex = LStorage.getOrDefault(this.storagePrefix + 'activeSidebarTabIndex', 0);
        const tripsColumnsHidden: string = LStorage.getItem(this.storagePrefix + 'tripsColumnsHidden');

        this.showGeofenceNamesOnMap = LStorage.getOrDefault(this.storagePrefix + 'showGeofenceNamesOnMap', true);
        this.compactListViews = LStorage.getOrDefault(this.storagePrefix + 'compactListViews', false);
        this.vehicles.sortKey = LStorage.getOrDefault(this.storagePrefix + 'vehiclesSortKey', 'name');
        this.geofences.sortKey = LStorage.getOrDefault(this.storagePrefix + 'geofencesSortKey', 'title');

        if (baselayerIndex) {
            mapWrapper.tileLayers.activateByIdOrUuid(baselayerIndex);

            if (baselayerOpacity) {
                mapWrapper.tileLayers.setOpacity(baselayerIndex, parseInt(baselayerOpacity));
            }
        }

        if (bgMapsOpacity) {
            this.bgMapsOpacity = parseInt(bgMapsOpacity);
        }

        if (typeof tripsColumnsHidden === 'string') {
            this.tripsColumnsHidden = tripsColumnsHidden.split(',');
        }
        if ($.isNumeric(sidebarPanelIndex)) {
            this.activeSidebarTabIndex = sidebarPanelIndex;
        }

        //if (this.stats) {
        //    this.stats.groupBy = LStorage.getOrDefault(this.storagePrefix + 'groupBy', 'week') as GroupRange;
        //}
    }

    @action.bound
    loadStateFromUrl() {
        const vars = utils.getHashVars();
        const panel = vars.panel;

        if (panel == 'trips' || panel == 'stats') {
            this.activePanel = panel;
        }

        this.tripsFilter.setFromUrlString(vars);
    }
}
